import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronDown, Hospital, Info, ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none w-full bg-white border border-[#E5DCC4] rounded-lg h-11 px-3.5 pr-9 text-[13px] text-[#1A2622] outline-none cursor-pointer focus:border-[#34538F]">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]" />
    </div>
  );
}

function Star({ r }) {
  if (r == null) return <span className="text-[10.5px] text-[#A8B0AA]">—</span>;
  return <span className="font-semibold text-[#A06628]">{r}<span className="text-[#D4A85C] ml-0.5">★</span></span>;
}

export default function HospitalsPage() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [state, setState] = useState(params.get('state') || '');
  const [type, setType] = useState('');
  const [minRating, setMinRating] = useState('');
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const LIMIT = 40;

  useEffect(() => { setPage(0); }, [q, state, type, minRating, sort]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.hospitals({
        q: q || undefined,
        state: state || undefined,
        type: type || undefined,
        min_rating: minRating || undefined,
        sort,
        limit: LIMIT,
        offset: page * LIMIT,
      }).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, state, type, minRating, sort, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Hospitals' }]} />

        <header className="mt-2 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A2622]">Hospital Directory</h1>
            <p className="mt-2 text-[13px] text-[#5A6660] max-w-[640px]">
              Every CMS-certified acute-care hospital in the U.S. Cross-referenced with our SNF base to surface
              <strong> referral catchment</strong>, <strong>shared rounding physicians</strong>, and <strong>quality signals</strong>.
            </p>
          </div>
          <Link to="/hospital-intelligence" className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-md bg-[#34538F] text-white text-[12.5px] hover:bg-[#1A2622]" data-testid="hosp-intel-link">
            Hospital Intelligence dashboard <ArrowUpRight size={13} />
          </Link>
        </header>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          <div className="relative md:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A857F]" />
            <input
              data-testid="hosp-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search hospital or city…"
              className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 pl-9 pr-3 text-[13px] outline-none focus:border-[#34538F]"
            />
          </div>
          <Select testId="hosp-filter-state" value={state} onChange={setState} options={US_STATES} placeholder="State" />
          <Select testId="hosp-filter-type" value={type} onChange={setType} options={['Acute Care Hospital', 'Critical Access Hospitals', 'Childrens', 'Psychiatric', 'Department of Defense']} placeholder="Type" />
          <Select testId="hosp-filter-rating" value={minRating} onChange={setMinRating} options={[
            { value: '5', label: '5★ only' },
            { value: '4', label: '4★ +' },
            { value: '3', label: '3★ +' },
          ]} placeholder="Min rating" />
        </div>

        <div className="flex items-center gap-2 mb-3 text-[12.5px] text-[#7A857F]">
          <span data-testid="hosp-result-count">{fmtInt(data.total)} hospitals</span>
          <span className="ml-auto flex items-center gap-2">
            Sort:
            <select data-testid="hosp-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-[#E5DCC4] rounded h-8 px-2 text-[12px]">
              <option value="name">Name</option>
              <option value="rating">Rating ↓</option>
              <option value="state">State</option>
            </select>
          </span>
        </div>

        {/* Table */}
        <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Hospital</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-left px-4 py-2.5">Ownership</th>
                  <th className="text-left px-4 py-2.5">City / State</th>
                  <th className="text-left px-4 py-2.5">Rating</th>
                  <th className="text-left px-4 py-2.5">Readmissions</th>
                  <th className="text-left px-4 py-2.5">ED</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-[#7A857F]">Loading…</td></tr>
                )}
                {!loading && data.items.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-[#7A857F]">No hospitals match these filters.</td></tr>
                )}
                {!loading && data.items.map((h) => (
                  <tr key={h.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`hosp-list-${h.ccn}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/hospitals/${h.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{h.name}</Link>
                      {h.trade_names?.[0] && <div className="text-[11.5px] text-[#7A857F]" data-testid={`hospital-aka-${h.ccn}`}>aka {h.trade_names[0]}</div>}
                      <div className="text-[10.5px] text-[#7A857F]">CCN {h.ccn} · {h.address || ''}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[#3A4A44]"><div className="truncate max-w-[180px]" title={h.type}>{h.type || '—'}</div></td>
                    <td className="px-4 py-2.5 text-[#3A4A44]"><div className="truncate max-w-[180px]" title={h.ownership}>{h.ownership || '—'}</div></td>
                    <td className="px-4 py-2.5 text-[#3A4A44]">{h.city}, {h.state}</td>
                    <td className="px-4 py-2.5"><Star r={h.overall_rating} /></td>
                    <td className="px-4 py-2.5 text-[11.5px] text-[#3A4A44]">
                      {h.readm_better ? <span className="text-[#2D5F4F]">▲{h.readm_better}</span> : null}
                      {h.readm_worse ? <span className="text-[#C75D5D] ml-1">▼{h.readm_worse}</span> : null}
                      {!h.readm_better && !h.readm_worse && <span className="text-[#A8B0AA]">—</span>}
                    </td>
                    <td className="px-4 py-2.5">{h.emergency_services ? <span className="text-[#2D5F4F]">Yes</span> : <span className="text-[#A8B0AA]">No</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] text-[12px]">
            <span className="text-[#7A857F]">Page {page + 1} of {totalPages}</span>
            <div className="flex gap-2">
              <button data-testid="hosp-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-3 h-8 rounded border border-[#E5DCC4] disabled:opacity-40">Prev</button>
              <button data-testid="hosp-next" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 h-8 rounded border border-[#E5DCC4] disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>
            <strong>Source:</strong> {data.source || 'CMS Hospital General Information'}. Updated quarterly by CMS.
          </span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
